// MorrowOS usage page — a faithful copy of the original T3 usage LAYOUT (same
// sections, classes, graph, metric grid, breakdown table) but fed entirely by
// REAL MorrowOS data: plan access/limits (entitlement engine) + the actual models
// the agent ran, priced at full API rate (usage ledger via GET /billing/usage).
// Models are generic (any id), so the T3 provider enum/marks are replaced with a
// per-model colour.
import { RefreshCwIcon, SparklesIcon, TriangleAlertIcon } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";

import { cn } from "../../lib/utils";
import { isElectron } from "../../env";
import { fetchUsage, type UsageSummary } from "../../morrowBilling";
import {
  syncEntitlements,
  useEntitlements,
  type EntitlementCapability,
} from "../../morrowEntitlements";
import { openUpgradeSuccessModal } from "../../upgradeModal";
import { ScrollArea } from "../ui/scroll-area";
import { Button } from "../ui/button";
import { SidebarInset } from "../ui/sidebar";
import { WorkspaceBreadcrumb, WorkspaceBreadcrumbItem } from "../WorkspaceBreadcrumb";
import { COLLAPSED_SIDEBAR_TITLEBAR_INSET_CLASS } from "../../workspaceTitlebar";

const WINDOW_OPTIONS = [
  { days: 7, label: "7 days" },
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
] as const;

// A small fixed palette assigned per model (by rank) — the T3 UI colours each
// provider; we colour each model.
const MODEL_COLORS = [
  "#6366f1",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#06b6d4",
  "#ec4899",
  "#84cc16",
];

function formatUsd(value: number): string {
  return `$${value < 1 ? value.toFixed(3) : value.toFixed(2)}`;
}
function formatTokens(value: number): string {
  if (value >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(2)}B`;
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(2)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}k`;
  return new Intl.NumberFormat().format(Math.round(value));
}
function formatCount(value: number): string {
  return new Intl.NumberFormat().format(Math.round(value));
}
/** Format a megabyte value as MB or GB (used for vault storage). */
function formatStorageMb(mb: number): string {
  if (mb >= 1024) {
    const gb = mb / 1024;
    return `${gb % 1 === 0 ? gb.toFixed(0) : gb.toFixed(1)} GB`;
  }
  if (mb > 0 && mb < 1) return `${(mb * 1024).toFixed(0)} KB`;
  return `${mb % 1 === 0 ? mb.toFixed(0) : mb.toFixed(1)} MB`;
}
function formatPercent(share: number): string {
  return `${(share * 100).toFixed(1)}%`;
}
/** The monthly AI $-budget capability — rendered as a dedicated hero card, in USD. */
const AI_VALUE_CAP = "ai.value.monthly";
/** Money for the AI budget: whole dollars when exact, else two decimals. */
function formatMoney(value: number): string {
  return `$${value.toLocaleString("en-US", {
    minimumFractionDigits: value % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
}
/** First of next calendar month (UTC), when the monthly AI budget resets. */
function nextMonthReset(): string {
  const now = new Date();
  const reset = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
  return reset.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
/**
 * Friendlier model label: drop the path (`@cf/zai-org/…`) and a leading vendor
 * prefix (`anthropic.`, `openai.`, `google.`) — but never cut at a version dot, which
 * turned glm-5.3-flash into "3-flash" and glm-5.2 into "2".
 */
export function modelLabel(model: string): string {
  const slash = model.lastIndexOf("/");
  const base = slash >= 0 ? model.slice(slash + 1) : model;
  return base.replace(/^[a-z]+\.(?=[a-z])/i, "");
}

// Order the capability categories appear in the plan matrix (mirrors the server
// catalog's CAPABILITY_CATEGORY_ORDER). Labels themselves come from the server.
const CATEGORY_ORDER = [
  "Models & AI",
  "Agents & automation",
  "Tools & connectors",
  "Browser & compute",
  "Storage & knowledge",
  "Collaboration & org",
  "Limits & platform",
  "Other",
] as const;

// Capacity capabilities are a *ceiling* (max), not a per-period tally — so they
// render as "N", never as an "X / N used" bar with a running count. Sub-agents =
// how many the agent can launch per request; Agents = how many distinct agents you can create.
const CAPACITY_CAPS = new Set<string>(["subagents.concurrent", "agents.max", "seats"]);
const CAPACITY_COPY: Record<
  string,
  { suffix: string; caption: (n: string) => string; unlimited: string }
> = {
  "subagents.concurrent": {
    suffix: "per request",
    caption: (n) => `Up to ${n} sub-agents per request`,
    unlimited: "Unlimited sub-agents per request",
  },
  "agents.max": {
    suffix: "max",
    caption: (n) => `Create up to ${n} distinct agents`,
    unlimited: "Create unlimited agents",
  },
  seats: {
    suffix: "seats",
    caption: (n) => `${n} member seats included`,
    unlimited: "Unlimited member seats",
  },
};

// The three model-tier booleans collapse into ONE "AI models" row so we show the
// access LEVEL a plan grants, not a teasing list of tiers it doesn't.
const MODEL_TIER_KEYS = new Set(["model.tier.pay", "model.tier.super", "model.tier.max"]);
function modelAccessLevel(caps: ReadonlyArray<EntitlementCapability>): string {
  const on = (k: string) => caps.find((c) => c.capability === k)?.bool === true;
  if (on("model.tier.max")) return "All models";
  if (on("model.tier.super")) return "Advanced + standard";
  if (on("model.tier.pay")) return "Standard models";
  return "Free models";
}

/**
 * One capability's plan state for the matrix: a concrete value when the plan
 * grants it, else "Not included". We deliberately show only what your plan can
 * and can't do — anything the plan doesn't grant (including not-yet-built
 * features) reads simply as "Not included", never a "coming soon" tease.
 */
function capabilityState(cap: EntitlementCapability): {
  label: string;
  tone: "on" | "off";
} {
  // Synthetic "AI models" row: the access level rides on `unit`.
  if (cap.capability === "model.access") {
    return { label: cap.unit ?? "Free models", tone: "on" };
  }
  // Not built/enforced yet, or a boolean the plan doesn't grant → Not included.
  if (cap.comingSoon) return { label: "Not included", tone: "off" };
  if (cap.type === "boolean") {
    return cap.bool ? { label: "Included", tone: "on" } : { label: "Not included", tone: "off" };
  }
  if (!cap.granted) return { label: "Not included", tone: "off" };
  if (cap.limit === null) return { label: "Unlimited", tone: "on" };
  if (cap.capability === AI_VALUE_CAP) {
    // The plan's monthly budget; extra credit is a one-off top-up, not part of the plan.
    return { label: `${formatMoney(cap.planLimit ?? cap.limit)}/mo`, tone: "on" };
  }
  if (cap.type === "storage") return { label: formatStorageMb(cap.limit), tone: "on" };
  if (cap.capability === "subagents.concurrent") {
    return { label: `${formatCount(cap.limit)} per request`, tone: "on" };
  }
  if (cap.type === "usage") return { label: `${formatCount(cap.limit)}/mo`, tone: "on" };
  return {
    label: `${formatCount(cap.limit)}${cap.unit ? ` ${cap.unit}` : ""}`,
    tone: "on",
  };
}

/** A single capability row inside the plan matrix. */
function CapabilityRow({ cap }: { readonly cap: EntitlementCapability }) {
  const st = capabilityState(cap);
  return (
    <div className="flex items-center justify-between gap-3 bg-background px-4 py-3">
      <div className="flex min-w-0 flex-col">
        <span className="truncate text-sm text-foreground">{cap.name}</span>
        {cap.description ? (
          <span className="truncate text-xs text-muted-foreground">{cap.description}</span>
        ) : null}
      </div>
      <span
        className={cn(
          "shrink-0 whitespace-nowrap text-xs font-medium",
          st.tone === "on" ? "text-foreground" : "text-muted-foreground",
        )}
      >
        {st.label}
      </span>
    </div>
  );
}

export function MorrowUsageAnalytics() {
  const entitlements = useEntitlements();
  const navigate = useNavigate();
  // Upgrade → our Packages page (plan comparison + checkout). Manage billing →
  // the dedicated Billing page (invoices, payment method, cancel).
  const goToPackages = useCallback(() => void navigate({ to: "/settings/packages" }), [navigate]);
  const goToBilling = useCallback(() => void navigate({ to: "/settings/billing" }), [navigate]);

  const [days, setDays] = useState(30);
  const [metric, setMetric] = useState<"cost" | "tokens">("cost");
  const [usage, setUsage] = useState<UsageSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [checkoutSucceeded, setCheckoutSucceeded] = useState(false);

  // Stripe returns to `/usage?checkout=success`. The entitlement store is cached,
  // so without an explicit re-fetch here the just-purchased plan never appears —
  // the user lands back on the app still showing Free with no confirmation. On a
  // success return: re-fetch entitlements (twice, spaced, so we also catch the
  // `checkout.session.completed` / subscription webhook if it lands a beat later),
  // show a confirmation banner, and strip the query param so a reload/back doesn't
  // replay it.
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("checkout") !== "success") return;
    setCheckoutSucceeded(true);
    // Authoritative self-heal: reconcile straight from Stripe (doesn't depend on
    // the webhook landing), THEN pop the celebration once the new plan is live in
    // the store so its copy names the right plan. One retry a beat later for
    // Stripe search's eventual consistency right after checkout.
    void syncEntitlements().then(() => openUpgradeSuccessModal());
    const retry = setTimeout(() => void syncEntitlements(), 3000);
    void navigate({ to: "/usage", replace: true });
    return () => clearTimeout(retry);
    // Run once on mount for the return trip; navigate is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(
    async (quiet = false) => {
      if (!quiet) setLoading(true);
      setUsage(await fetchUsage(days));
      if (!quiet) setLoading(false);
    },
    [days],
  );
  useEffect(() => {
    void load();
  }, [load]);

  const models = usage?.models ?? [];
  const totals = usage?.totals;
  const totalCost = totals?.totalCostUsd ?? 0;
  const totalTokens = totals?.totalTokens ?? 0;

  // Rank models by the active metric so the bars descend, like the T3 page.
  const ranked = useMemo(
    () =>
      [...models].sort((a, b) =>
        metric === "cost"
          ? (b.costUsd ?? 0) - (a.costUsd ?? 0)
          : b.inputTokens + b.outputTokens - (a.inputTokens + a.outputTokens),
      ),
    [models, metric],
  );

  // "Usage this period": the numeric capabilities the user actually HAS (granted,
  // live, not coming-soon) — agent turns, tool calls, sub-agent capacity,
  // connectors, storage. Everything else lives in the full capability matrix below.
  const meteredCaps = entitlements.capabilities.filter(
    (c) =>
      c.granted &&
      !c.comingSoon &&
      c.metered &&
      c.type !== "boolean" &&
      c.capability !== AI_VALUE_CAP,
  );
  // The monthly AI $-budget gets its own hero card (in dollars), not a generic
  // integer meter — it's the headline number.
  const aiBudget = entitlements.capabilities.find(
    (c) => c.capability === AI_VALUE_CAP && c.granted,
  );
  // Collapse the three model-tier booleans into one "AI models" row that states
  // the access LEVEL (the level string rides on `unit`, which capabilityState reads).
  const modelsRow: EntitlementCapability = {
    capability: "model.access",
    name: "AI models",
    description: "Which models your agents can run.",
    type: "boolean",
    bool: true,
    granted: true,
    comingSoon: false,
    category: "Models & AI",
    unit: modelAccessLevel(entitlements.capabilities),
    limit: null,
    used: 0,
    remaining: null,
    metered: false,
    planLimit: null,
    extra: 0,
  };
  // The whole-product matrix: every capability grouped by category, in order. In
  // "Models & AI" the raw model.tier.* rows are replaced by the single models row.
  const categories = CATEGORY_ORDER.map((category) => {
    const caps = entitlements.capabilities.filter(
      (c) => (c.category ?? "Other") === category && !MODEL_TIER_KEYS.has(c.capability),
    );
    return { category, caps: category === "Models & AI" ? [modelsRow, ...caps] : caps };
  }).filter((g) => g.caps.length > 0);

  return (
    <SidebarInset className="h-dvh min-h-0 overflow-hidden overscroll-y-none bg-background text-foreground isolate">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-background text-foreground">
        {!isElectron && (
          <header
            className={cn(
              "flex h-[var(--workspace-topbar-height)] min-h-[var(--workspace-topbar-height)] shrink-0 items-center justify-between px-3 sm:px-5",
              COLLAPSED_SIDEBAR_TITLEBAR_INSET_CLASS,
            )}
          >
            <WorkspaceBreadcrumb ariaLabel="Usage breadcrumb">
              <WorkspaceBreadcrumbItem current>Plan &amp; Usage</WorkspaceBreadcrumbItem>
            </WorkspaceBreadcrumb>
            <div className="flex items-center gap-1">
              <Button variant="ghost-muted" size="sm" onClick={goToPackages}>
                Upgrade
              </Button>
              <Button variant="ghost-muted" size="sm" onClick={goToBilling}>
                Manage billing
              </Button>
            </div>
          </header>
        )}

        <ScrollArea className="min-h-0 flex-1">
          <div className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 py-6">
            {checkoutSucceeded && (
              <div className="flex items-start justify-between gap-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-700 dark:text-emerald-300">
                <span>
                  Payment confirmed — your new plan is active. If it doesn’t show below yet, it’ll
                  appear in a moment.
                </span>
                <button
                  type="button"
                  onClick={() => setCheckoutSucceeded(false)}
                  className="shrink-0 rounded px-1 text-emerald-700/70 hover:text-emerald-700 dark:text-emerald-300/70 dark:hover:text-emerald-300"
                  aria-label="Dismiss"
                >
                  ×
                </button>
              </div>
            )}
            <p className="text-[11px] font-semibold uppercase tracking-[0.04em] text-muted-foreground">
              {!entitlements.isLoaded
                ? "Loading your plan…"
                : entitlements.viewer.isOrg
                  ? `Your organization · ${entitlements.planName} plan`
                  : `${entitlements.planName} plan · ${entitlements.tier} tier`}
            </p>

            {/* While the (Clerk-gated) entitlements load, show skeleton cards
                instead of a blank page — far users otherwise stare at empty space. */}
            {!entitlements.isLoaded ? (
              <div className="flex flex-col gap-3" aria-hidden>
                <div className="h-4 w-40 animate-pulse rounded bg-muted" />
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div
                      key={i}
                      className="h-24 animate-pulse rounded-lg border border-border bg-muted/40"
                    />
                  ))}
                </div>
              </div>
            ) : null}

            {/* Plan access & usage: everything you have access to, vs used. Company
                usage is admin-only, so a non-admin member sees a note instead. */}
            {entitlements.viewer.isOrg && !entitlements.viewer.usageVisible ? (
              <section className="rounded-lg border border-border bg-card/40 px-4 py-3">
                <h2 className="text-sm font-medium text-foreground">Usage this period</h2>
                <p className="mt-1 text-xs text-muted-foreground">
                  Your company's usage is visible to organization admins. Ask an admin for usage and
                  billing details.
                </p>
              </section>
            ) : aiBudget || meteredCaps.length > 0 ? (
              <section className="flex flex-col gap-3">
                <div className="flex items-baseline gap-2">
                  <h2 className="text-sm font-medium text-foreground">Usage this period</h2>
                  {entitlements.viewer.isOrg ? (
                    <span className="text-xs text-muted-foreground">
                      · across your organization
                    </span>
                  ) : null}
                </div>
                {aiBudget ? (
                  <AiBudgetCard cap={aiBudget} isOrg={entitlements.viewer.isOrg} />
                ) : null}
                {meteredCaps.length > 0 ? (
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {meteredCaps.map((cap) => (
                      <MeteredCard
                        key={cap.capability}
                        cap={cap}
                        isOrg={entitlements.viewer.isOrg}
                      />
                    ))}
                  </div>
                ) : null}
              </section>
            ) : null}

            {/* Everything the product does — grouped, with each capability's state
                for this plan (a limit, Included / Not included, or Coming soon). */}
            {categories.length > 0 ? (
              <section className="flex flex-col gap-6">
                <div className="flex items-baseline justify-between">
                  <h2 className="text-sm font-medium text-foreground">
                    Everything in {entitlements.planName}
                  </h2>
                  <button
                    type="button"
                    onClick={goToPackages}
                    className="text-xs text-primary hover:underline"
                  >
                    Compare plans →
                  </button>
                </div>
                {categories.map((group) => (
                  <div key={group.category} className="flex flex-col gap-2">
                    <h3 className="text-[11px] font-semibold uppercase tracking-[0.04em] text-muted-foreground">
                      {group.category}
                    </h3>
                    <div className="grid grid-cols-1 gap-px overflow-hidden rounded-lg border border-border bg-border sm:grid-cols-2">
                      {group.caps.map((cap) => (
                        <CapabilityRow key={cap.capability} cap={cap} />
                      ))}
                    </div>
                  </div>
                ))}
              </section>
            ) : null}

            {/* AI usage & cost — admin-only for organizations. */}
            {entitlements.viewer.usageVisible ? (
              <>
                {/* Window selector + refresh */}
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <h2 className="text-sm font-medium text-foreground">AI usage &amp; cost</h2>
                  <div className="flex items-center gap-2">
                    <div className="flex rounded-md border border-border">
                      {WINDOW_OPTIONS.map((option) => (
                        <button
                          key={option.days}
                          type="button"
                          aria-pressed={option.days === days}
                          onClick={() => setDays(option.days)}
                          className={cn(
                            "cursor-pointer px-3 py-1.5 text-xs first:rounded-s-md last:rounded-e-md",
                            option.days === days
                              ? "bg-muted text-foreground"
                              : "text-muted-foreground hover:text-foreground",
                          )}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                    <Button
                      size="icon"
                      variant="outline"
                      onClick={() => void load()}
                      aria-label="Refresh usage"
                    >
                      <RefreshCwIcon className={cn("size-3.5", loading && "animate-spin")} />
                    </Button>
                  </div>
                </div>

                {/* Cost headline + per-model breakdown (left) · daily chart (right) */}
                <section className="grid gap-6 lg:grid-cols-[minmax(0,20rem)_minmax(0,1fr)]">
                  <div className="flex flex-col gap-5">
                    <div className="flex flex-col gap-1">
                      <span className="text-xs tracking-wide text-muted-foreground uppercase">
                        {metric === "cost" ? "Raw token cost" : "Processed tokens"}
                      </span>
                      <span className="text-4xl font-semibold text-foreground tabular-nums">
                        {metric === "cost" ? `${formatUsd(totalCost)}*` : formatTokens(totalTokens)}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {metric === "cost"
                          ? "* if billed at full API rate"
                          : `Input + output across ${formatCount(totals?.events ?? 0)} calls.`}
                      </span>
                    </div>

                    {ranked.map((m, i) => {
                      const mTokens = m.inputTokens + m.outputTokens;
                      const share =
                        metric === "cost"
                          ? totalCost === 0
                            ? 0
                            : (m.costUsd ?? 0) / totalCost
                          : totalTokens === 0
                            ? 0
                            : mTokens / totalTokens;
                      const color = MODEL_COLORS[i % MODEL_COLORS.length];
                      return (
                        <div key={m.model} className="flex flex-col gap-1.5">
                          <div className="flex items-baseline justify-between">
                            <span className="flex items-center gap-2 text-sm text-foreground">
                              <span
                                className="size-2.5 shrink-0 rounded-full"
                                style={{ backgroundColor: color }}
                                aria-hidden
                              />
                              <span className="truncate" title={m.model}>
                                {modelLabel(m.model)}
                              </span>
                            </span>
                            <span className="shrink-0 text-sm text-foreground tabular-nums">
                              {metric === "cost"
                                ? formatUsd(m.costUsd ?? 0)
                                : formatTokens(mTokens)}
                            </span>
                          </div>
                          <div className="h-1 w-full overflow-hidden rounded-full bg-muted">
                            <div
                              className="h-full"
                              style={{
                                width: `${(share * 100).toFixed(1)}%`,
                                backgroundColor: color,
                              }}
                            />
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {metric === "cost"
                              ? `${formatPercent(share)} of cost · ${formatTokens(mTokens)} tokens${m.priced ? "" : " · unpriced"}`
                              : `${formatPercent(share)} of tokens · ${formatUsd(m.costUsd ?? 0)}`}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  <div className="flex flex-col gap-3">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <h3 className="text-sm font-medium text-foreground">
                        Daily {metric === "cost" ? "cost" : "tokens"}
                      </h3>
                      <div className="flex overflow-hidden rounded-md border border-border">
                        {(["cost", "tokens"] as const).map((option) => (
                          <button
                            key={option}
                            type="button"
                            onClick={() => setMetric(option)}
                            className={cn(
                              "cursor-pointer px-2.5 py-1 text-[10px] tracking-wide uppercase",
                              option === metric
                                ? "bg-muted text-foreground"
                                : "text-muted-foreground hover:text-foreground",
                            )}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    </div>
                    <DailyChart daily={usage?.daily ?? []} metric={metric} loading={loading} />
                  </div>
                </section>

                {/* Metric grid */}
                <section className="grid grid-cols-2 gap-px border-y border-border bg-border md:grid-cols-4">
                  <Metric
                    label="Processed tokens"
                    value={formatTokens(totalTokens)}
                    detail={`${formatCount(totals?.events ?? 0)} calls`}
                  />
                  <Metric
                    label="Input tokens"
                    value={formatTokens(totals?.inputTokens ?? 0)}
                    detail="sent to models"
                  />
                  <Metric
                    label="Output tokens"
                    value={formatTokens(totals?.outputTokens ?? 0)}
                    detail="generated"
                  />
                  <Metric
                    label="Full-rate cost"
                    value={`${formatUsd(totalCost)}`}
                    detail="if billed at API rate"
                  />
                </section>

                {/* Breakdown table: Model · Cost · Share · Tokens */}
                <section className="flex flex-col gap-3">
                  <h2 className="text-sm font-medium text-foreground">Breakdown</h2>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border text-left text-xs text-muted-foreground">
                        <th className="py-2 font-normal">Model</th>
                        <th className="py-2 text-right font-normal">Cost</th>
                        <th className="py-2 text-right font-normal">Share</th>
                        <th className="py-2 text-right font-normal">Tokens</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ranked.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="py-6 text-center text-muted-foreground">
                            {loading ? "Loading…" : "No usage recorded in this window yet."}
                          </td>
                        </tr>
                      ) : (
                        ranked.map((m, i) => {
                          const mTokens = m.inputTokens + m.outputTokens;
                          const share = totalCost === 0 ? 0 : (m.costUsd ?? 0) / totalCost;
                          return (
                            <tr key={m.model} className="border-b border-border/50">
                              <td className="py-2 text-foreground">
                                <span className="flex items-center gap-2">
                                  <span
                                    className="size-2.5 shrink-0 rounded-full"
                                    style={{
                                      backgroundColor: MODEL_COLORS[i % MODEL_COLORS.length],
                                    }}
                                    aria-hidden
                                  />
                                  {modelLabel(m.model)}
                                </span>
                              </td>
                              <td className="py-2 text-right text-foreground tabular-nums">
                                {m.priced ? formatUsd(m.costUsd ?? 0) : "—"}
                              </td>
                              <td className="py-2 text-right text-muted-foreground tabular-nums">
                                {formatPercent(share)}
                              </td>
                              <td className="py-2 text-right text-muted-foreground tabular-nums">
                                {formatTokens(mTokens)}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                  {totals && totals.unpricedModels > 0 ? (
                    <p className="text-xs text-muted-foreground">
                      {totals.unpricedModels} model{totals.unpricedModels === 1 ? "" : "s"} not in
                      the rate table — shown without a cost estimate.
                    </p>
                  ) : null}
                </section>
              </>
            ) : null}
          </div>
        </ScrollArea>
      </div>
    </SidebarInset>
  );
}

/**
 * The headline monthly AI-budget card: dollars used of the plan's $ allowance,
 * with a threshold-coloured bar and the 50/80/95 warn states. Spend is real model
 * cost (µ$ on the server, already converted to USD by the entitlements summary),
 * so this is what an owner watches to know how much AI they have left this month.
 */
function AiBudgetCard({
  cap,
  isOrg,
}: {
  readonly cap: EntitlementCapability;
  readonly isOrg?: boolean;
}) {
  const unlimited = cap.limit === null;
  const limit = cap.limit ?? 0;
  const used = cap.used;
  const remaining = cap.remaining ?? Math.max(0, limit - used);
  const pctExact = unlimited || limit === 0 ? 0 : (used / limit) * 100;
  const pct = Math.min(100, Math.round(pctExact));
  const exhausted = !unlimited && remaining <= 0;
  // 50 / 80 / 95 thresholds → bar colour + status voice.
  const level = exhausted
    ? "over"
    : pct >= 95
      ? "crit"
      : pct >= 80
        ? "warn"
        : pct >= 50
          ? "half"
          : "ok";
  const barClass =
    level === "over" || level === "crit"
      ? "bg-red-500"
      : level === "warn"
        ? "bg-amber-500"
        : "bg-primary";
  const status = exhausted
    ? "Budget used up — top up or upgrade to keep working"
    : level === "crit"
      ? `Almost out — ${formatMoney(remaining)} left (${pct}%)`
      : level === "warn"
        ? `${formatMoney(remaining)} left · ${pct}% used`
        : level === "half"
          ? `Halfway — ${formatMoney(remaining)} left`
          : `${formatMoney(remaining)} left this month`;
  const alert = level === "over" || level === "crit" || level === "warn";
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-border bg-card/50 p-5">
      <div className="flex items-center justify-between gap-3">
        <span className="flex items-center gap-2 text-sm font-medium text-foreground">
          <SparklesIcon className="size-4 text-primary" aria-hidden />
          {cap.name}
        </span>
        {!unlimited ? (
          <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs text-muted-foreground">
            Resets {nextMonthReset()}
          </span>
        ) : null}
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="text-3xl font-semibold tabular-nums text-foreground">
          {formatMoney(used)}
        </span>
        <span className="text-base tabular-nums text-muted-foreground">
          {unlimited ? "used" : `of ${formatMoney(limit)}`}
        </span>
      </div>
      {unlimited ? (
        <span className="text-xs text-muted-foreground">Unlimited AI budget on your plan</span>
      ) : (
        <>
          <div className="h-2.5 w-full overflow-hidden rounded-full bg-muted">
            <div
              className={cn("h-full rounded-full transition-all", barClass)}
              style={{ width: `${Math.max(pct, used > 0 ? 2 : 0)}%` }}
            />
          </div>
          <span
            className={cn(
              "flex items-center gap-1.5 text-xs",
              alert ? "text-amber-600 dark:text-amber-400" : "text-muted-foreground",
              (level === "over" || level === "crit") && "text-red-600 dark:text-red-400",
            )}
          >
            {alert ? <TriangleAlertIcon className="size-3.5 shrink-0" aria-hidden /> : null}
            {status}
          </span>
        </>
      )}
      {!unlimited && cap.extra > 0 && cap.planLimit !== null ? (
        <span className="text-xs text-muted-foreground">
          {formatMoney(cap.planLimit)} plan budget + {formatMoney(cap.extra)} extra credit
        </span>
      ) : null}
      <span className="text-xs text-muted-foreground">
        {isOrg ? "Shared across your organization · " : ""}Spend it at any pace — real model cost.
      </span>
    </div>
  );
}

function MeteredCard({
  cap,
  isOrg,
}: {
  readonly cap: EntitlementCapability;
  readonly isOrg?: boolean;
}) {
  const unlimited = cap.limit === null;
  const limit = cap.limit ?? 0;

  // Capacity capability (a ceiling, not a running tally): show the max, not usage.
  if (CAPACITY_CAPS.has(cap.capability)) {
    const copy = CAPACITY_COPY[cap.capability] ?? {
      suffix: cap.unit ?? "",
      caption: (n: string) => `Up to ${n}`,
      unlimited: "Unlimited",
    };
    return (
      <div className="flex flex-col gap-2 rounded-lg border border-border bg-card/40 p-4">
        <span className="text-sm text-foreground">{cap.name}</span>
        <div className="flex items-baseline gap-1.5">
          <span className="text-2xl font-semibold tabular-nums text-foreground">
            {unlimited ? "∞" : formatCount(limit)}
          </span>
          <span className="text-sm tabular-nums text-muted-foreground">
            {unlimited ? "" : copy.suffix}
          </span>
        </div>
        <span className="text-xs text-muted-foreground">
          {unlimited ? copy.unlimited : copy.caption(formatCount(limit))}
        </span>
      </div>
    );
  }
  const isStorage = cap.type === "storage";
  const fmt = isStorage ? formatStorageMb : formatCount;
  const pct = unlimited || limit === 0 ? 0 : Math.min(100, Math.round((cap.used / limit) * 100));
  const exhausted = !unlimited && (cap.remaining ?? 0) <= 0;
  const near = !unlimited && !exhausted && pct >= 80;
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border bg-card/40 p-4">
      <span className="text-sm text-foreground">{cap.name}</span>
      <div className="flex items-baseline gap-1.5">
        <span className="text-2xl font-semibold tabular-nums text-foreground">{fmt(cap.used)}</span>
        <span className="text-sm tabular-nums text-muted-foreground">
          {unlimited ? "used" : `/ ${fmt(limit)}`}
        </span>
      </div>
      {unlimited ? (
        <span className="text-xs text-muted-foreground">Unlimited on your plan</span>
      ) : (
        <>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div
              className={cn(
                "h-full rounded-full",
                exhausted ? "bg-red-500" : near ? "bg-amber-500" : "bg-primary",
              )}
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className="text-xs text-muted-foreground">
            {exhausted ? "Limit reached" : `${fmt(cap.remaining ?? 0)} left · ${pct}% used`}
          </span>
        </>
      )}
      {isStorage && isOrg ? (
        <span className="text-xs text-muted-foreground">
          Your files + shared organization files
        </span>
      ) : null}
    </div>
  );
}

function Metric({
  label,
  value,
  detail,
}: {
  readonly label: string;
  readonly value: string;
  readonly detail: string;
}) {
  return (
    <div className="flex flex-col gap-0.5 bg-background px-4 py-3">
      <span className="text-xs text-muted-foreground uppercase tracking-wide">{label}</span>
      <span className="text-lg font-semibold text-foreground tabular-nums">{value}</span>
      <span className="text-xs text-muted-foreground">{detail}</span>
    </div>
  );
}

/** Simple area chart of the daily series, styled like the T3 chart. */
function DailyChart({
  daily,
  metric,
  loading,
}: {
  readonly daily: ReadonlyArray<{ date: string; costUsd: number; tokens: number }>;
  readonly metric: "cost" | "tokens";
  readonly loading: boolean;
}) {
  const points = daily.map((d) => (metric === "cost" ? d.costUsd : d.tokens));
  const max = Math.max(1, ...points);
  const W = 600;
  const H = 180;
  const n = points.length;

  if (loading) {
    return <div className="h-[180px] w-full animate-pulse rounded-md bg-muted/50" aria-hidden />;
  }
  if (n < 2) {
    return (
      <div className="flex h-[180px] w-full items-center justify-center rounded-md border border-dashed border-border text-xs text-muted-foreground">
        Not enough data yet for a daily chart.
      </div>
    );
  }

  const step = W / (n - 1);
  const y = (v: number) => H - (v / max) * (H - 8) - 4;
  const line = points.map((v, i) => `${i * step},${y(v)}`).join(" ");
  const area = `0,${H} ${line} ${W},${H}`;
  const peak = Math.max(...points);

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{daily[0]?.date}</span>
        <span>peak {metric === "cost" ? formatUsd(peak) : formatTokens(peak)}</span>
        <span>{daily[n - 1]?.date}</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="h-[180px] w-full">
        <polygon points={area} fill="var(--color-primary)" opacity="0.12" />
        <polyline
          points={line}
          fill="none"
          stroke="var(--color-primary)"
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    </div>
  );
}
